import React from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import * as Icon from 'react-bootstrap-icons';
import type { TFunction } from 'i18next';

import { Grid } from '../../../common/components/Grid/Grid';
import {
  GridColumn,
  GridCountFunction,
  GridGetterFunction,
} from '../../../common/components/Grid/GridDataTypes';
import momencik from '../../../common/momencik';
import { TKeys, useTranslation } from '../../../common/hooks/useTranslation';
import {
  SalesInvoiceStatusEnum,
  SalesInvoiceStatusObj,
  InvoiceTypeEnum,
  InvoiceTypeObj,
  PaymentFormObj,
} from '../../../common/pokCore/validation/enums';
import { buildProjectLabel } from '../../../utils/buildProjectLabel';
import numberFormatter from '../../../common/numberFormatter';
import { toDateOnly } from '../../../utils/dateUtils';

import { NewWindowButton } from './NewWindowButton';
import {
  GetSalesInvoiceWithPaymentDto,
  PaymentStatus,
} from './SalesInvoicePaymentStatus';

interface SalesInvoiceSearchProps {
  getData?: GridGetterFunction;
  getCount?: GridCountFunction;
  status?: SalesInvoiceStatusEnum;
  onRowClick?: (key: string) => Promise<void> | void | undefined;
  refresh?: boolean;
  withFilterInColumns?: boolean;
  gridMessage?: string;
  isImportView?: boolean;
  hideStatusColumn?: boolean;
  enableMultiSelect?: boolean;
  showNewWindowButton?: boolean;
  initialOrderDirection?: 'ASC' | 'DESC';
  withSymphonyDate?: boolean;
  selectedKeys?: string[];
  withPayment?: boolean;
}

export const salesInvoiceForGrid = (
  salesInvoice: GetSalesInvoiceWithPaymentDto,
  t: TFunction<'fvsLng', 'fvsLng'>,
  tk: TKeys<'fvsLng'>,
) => {
  return {
    key: salesInvoice.id,
    values: {
      'salesInvoice.systemNumber': salesInvoice.systemNumber,
      'salesInvoice.currency': salesInvoice.currency,
      'salesInvoice.number': (
        <>
          {salesInvoice.number}
          {salesInvoice.number && <br />}
          <OverlayTrigger
            placement="bottom"
            overlay={<Tooltip>{t(InvoiceTypeObj[salesInvoice.type])}</Tooltip>}
          >
            {salesInvoice.type === InvoiceTypeEnum.Foreign ? (
              <Icon.Globe2 className="icon-blue me-2" />
            ) : (
              <Icon.House className="icon-blue me-2" />
            )}
          </OverlayTrigger>
          {!!salesInvoice.parent && (
            <OverlayTrigger
              placement="bottom"
              overlay={
                <Tooltip>
                  {t(tk.finance.correctionToInvoice)}{' '}
                  {salesInvoice.originalInvoice?.number}
                </Tooltip>
              }
            >
              <Icon.FileEarmarkMinusFill className="icon-blue me-2" />
            </OverlayTrigger>
          )}
          {salesInvoice.company.symphonyHandel &&
            (!salesInvoice.isSymphonyCode ||
              (salesInvoice.recipientClient?.id &&
                !salesInvoice.isSymphonyCodeForRecipient)) && (
              <OverlayTrigger
                placement="bottom"
                overlay={
                  <Tooltip>{t(tk.finance.noSymphonyOrRecipientCode)}</Tooltip>
                }
              >
                <Icon.ExclamationCircle className="icon-red me-2" />
              </OverlayTrigger>
            )}
        </>
      ),
      'salesInvoice.invoiceDate': momencik(salesInvoice.invoiceDate),
      'salesInvoice.saleDate': momencik(salesInvoice.saleDate),
      'salesInvoice.sentToSymphonyHandelTs': momencik(
        salesInvoice.sentToSymphonyHandelTs,
      ),
      invoice_status: t(SalesInvoiceStatusObj[salesInvoice.status]),
      'salesInvoice.paymentForm': t(PaymentFormObj[salesInvoice.paymentForm]),
      'payableDays.value': salesInvoice.payableDays?.value,
      project_label: salesInvoice.salesInvoiceProjects
        ?.map(({ project }) => buildProjectLabel(project))
        .join(', '),
      'client.name': salesInvoice.purchaser?.client?.name,
      'client.nip': salesInvoice.purchaser?.client?.nip,
      'budget.name': salesInvoice.purchaser?.budget?.name,
      'salesInvoice.amountNetDelta': numberFormatter(
        salesInvoice.amountNetDelta || 0,
        true,
      ),
      paymentStatus: <PaymentStatus invoice={salesInvoice} />,
      newWindowButton: <NewWindowButton salesInvoiceId={salesInvoice.id} />,
    },
    isInactive: !salesInvoice.active,
    tdCssClass: {
      'salesInvoice.amountNetDelta': 'amounts-align-right',
    },
    exportValues: {
      'salesInvoice.invoiceDate': toDateOnly(salesInvoice.invoiceDate),
      'salesInvoice.saleDate': toDateOnly(salesInvoice.saleDate),
      'salesInvoice.sentToSymphonyHandelTs': toDateOnly(
        salesInvoice.sentToSymphonyHandelTs,
      ),
    },
  };
};

export const SalesInvoiceSearch: React.FC<SalesInvoiceSearchProps> = props => {
  const { t, tk } = useTranslation('fvsLng');

  const columns: GridColumn[] = [
    ...(props.showNewWindowButton
      ? [
          {
            header: '',
            property: 'newWindowButton',
            noSorting: true,
            noFilter: true,
          },
        ]
      : []),
    { header: t(tk.common.sysNumber), property: 'salesInvoice.systemNumber' },
    { header: t(tk.common.number), property: 'salesInvoice.number' },
    {
      header: t(tk.common.issueDate),
      property: 'salesInvoice.invoiceDate',
    },
    {
      header: t(tk.common.saleDate),
      property: 'salesInvoice.saleDate',
    },
    ...(props.isImportView
      ? []
      : [
          {
            header: t(tk.finance.paymentForm),
            property: 'salesInvoice.paymentForm',
            noFilter: true,
          },
          {
            header: t(tk.finance.paymentDate),
            property: 'payableDays.value',
          },
        ]),
    { header: t(tk.common.currency), property: 'salesInvoice.currency' },
    ...(props.hideStatusColumn
      ? []
      : [{ header: t(tk.common.status), property: 'invoice_status' }]),
    { header: t(tk.prLang.project), property: 'project_label' },
    { header: t(tk.finance.buyerName), property: 'client.name' },
    { header: t(tk.finance.nip), property: 'client.nip' },
    { header: t(tk.common.budget), property: 'budget.name' },
    {
      header: t(tk.finance.net),
      property: 'salesInvoice.amountNetDelta',
      exportType: 'number' as const,
    },
    ...(props.withPayment
      ? [
          {
            header: t(tk.finance.paymentStatus),
            property: 'paymentStatus',
            noSorting: true,
          },
        ]
      : []),
    ...(props.withSymphonyDate
      ? [
          {
            header: t(tk.finance.sentToSymphonyHandelTs),
            property: 'salesInvoice.sentToSymphonyHandelTs',
          },
        ]
      : []),
  ];

  return (
    <Grid
      getData={props.getData}
      getDataCount={props.getCount}
      columns={columns}
      showFilter={true}
      refresh={props.refresh}
      initialOrderColumn="salesInvoice.systemNumber"
      initialOrderDirection={props.initialOrderDirection || 'DESC'}
      availablePageSizes={[10, 20, 50]}
      emptyGridMessage={props.gridMessage || t(tk.finance.noInvoices)}
      onRowClick={props.onRowClick}
      withHistory={true}
      withFilterInColumns={props.withFilterInColumns}
      enableMultiSelect={props.enableMultiSelect}
      withOverflowXAuto={true}
      selectedKeys={props.selectedKeys}
    />
  );
};
